import React from 'react';
import { Skeleton } from '@material-ui/lab';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { makeStyles } from '@material-ui/core';
import { useResourcingChartToolbarContext } from '~/modules/projects/resourcing-plan/hooks';
import { useProjectContext } from '~/modules/resourcing/common/contexts';
import { UserRoleActualsTimeLineRow } from '~/modules/resourcing/common/components';
import ResourceAllocationChartTimeLineItem2 from '../ResourceAllocationChartTimeLineItem/ResourceAllocationChartTimeLineItem2';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    position: 'relative',
    overflow: 'hidden'
  },
  rootWithBorder: {
    borderTop: `1px solid ${theme.palette.divider}`
  },
  loadingRoot: {
    gap: theme.spacing(2),
    paddingTop: theme.spacing(2)
  },
  singleAllocationBar: {
    width: '100%',
    paddingTop: theme.spacing(1)
  }
}));

const useActualStyles = makeStyles(theme => ({
  root: ({ allocationsCount }) => ({
    minHeight: (allocationsCount + 1) * theme.spacing(4) + 8
  })
}));

const mergeUserScheduleDetails = ({
  allocation,
  userScheduleDetails: {
    scheduleDurationByPeriodMap,
    resourceAvailabilitySummaryByPeriodMap,
    holidays,
    timeoffs
  }
}) => ({
  ...allocation,
  user: {
    ...allocation.user,
    scheduleDurationByPeriodMap,
    resourceAvailabilitySummaryByPeriodMap,
    holidays,
    timeoffs
  }
});

export const ResourceAllocationChartTimeline = ({
  actualSummaryBlockPopupProps,
  actualsData,
  allocatedUser,
  chartDisplayPeriods,
  classes: classesOverride,
  handleRemoveResourceAllocation,
  loading,
  resourceAllocations,
  userScheduleDetails,
  index,
  setSnackBarState,
  otherProjectAllocationsSummary
}) => {
  const {
    scale,
    dateRange,
    isResourceActualModeEnabled
  } = useResourcingChartToolbarContext();

  const classes = useStyles({ classes: classesOverride });
  const actualClasses = useActualStyles({
    allocationsCount: resourceAllocations.length
  });

  const {
    projectObjectPermissions: { canEditResourceAllocation: isEditAllowed }
  } = useProjectContext();

  return (
    <div
      className={classNames(classes.root, {
        [actualClasses.root]: !loading && isResourceActualModeEnabled,
        [classes.loadingRoot]: loading,
        [classes.rootWithBorder]: index > 0
      })}
    >
      {loading ? (
        <Skeleton variant="rect" />
      ) : (
        (resourceAllocations || []).map(allocation => {
          return (
            <div key={allocation.id} className={classes.singleAllocationBar}>
              <ResourceAllocationChartTimeLineItem2
                chartDisplayPeriods={chartDisplayPeriods}
                handleRemoveResourceAllocation={handleRemoveResourceAllocation}
                setSnackBarState={setSnackBarState}
                allocatedUser={allocatedUser}
                isEditEnabled={isEditAllowed}
                otherProjectAllocationsSummary={otherProjectAllocationsSummary}
                resourceAllocation={mergeUserScheduleDetails({
                  allocation,
                  userScheduleDetails
                })}
              />
            </div>
          );
        })
      )}

      {isResourceActualModeEnabled &&
        (loading ? (
          <Skeleton variant="rect" />
        ) : (
          <UserRoleActualsTimeLineRow
            actualSummaryBlockPopupProps={actualSummaryBlockPopupProps}
            actualsData={actualsData}
            chartDisplayDateRange={dateRange}
            chartDisplayPeriods={chartDisplayPeriods}
            resourceAllocations={resourceAllocations}
            scale={scale}
            user={allocatedUser.user}
            userScheduleDetails={userScheduleDetails}
          />
        ))}
    </div>
  );
};

export default ResourceAllocationChartTimeline;

ResourceAllocationChartTimeline.propTypes = {
  actualSummaryBlockPopupProps: PropTypes.object,
  actualsData: PropTypes.object,
  allocatedUser: PropTypes.object,
  chartDisplayPeriods: PropTypes.array,
  classes: PropTypes.object,
  index: PropTypes.number,
  handleRemoveResourceAllocation: PropTypes.func,
  otherProjectAllocationsSummary: PropTypes.object,
  loading: PropTypes.bool.isRequired,
  resourceAllocations: PropTypes.array,
  userScheduleDetails: PropTypes.object.isRequired,
  setSnackBarState: PropTypes.func
};
